import { formatISO } from "date-fns";
import { getShortJournalEntryReferenceNumber } from "@common/helpers/accounting";
import { isLiveLadderPeriod } from "@common/helpers/domains/accounting/scheduleLadderPeriods";
import { isScheduleBalanceAdjustmentEntry } from "@common/helpers/domains/accounting/scheduleLifecycleEntries";
import { roundToCents } from "@common/helpers/monetary";
import {
    ScheduleApplicationListItem,
    ScheduleApplicationListJournalEntry,
    ScheduleApplicationListMilestone,
    ScheduleApplicationRelatedLink,
    ScheduleApplicationSourceLine,
    ScheduleApplicationSourceSummary,
} from "@common/schemas/domains/accounting/scheduleApplicationList.schema";
import {
    ExpenseSubMode,
    RevenueSubMode,
    ScheduleApplicationPeriodStatus,
    ScheduleApplicationStatus,
    ScheduleBillingStatus,
    ScheduleJeSubtype,
    ScheduleLifecycleFilterStatus,
    ScheduleSourceType,
    ScheduleTermUnit,
} from "@common/types/domains/accounting/schedule";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { InvoiceViewOptionGroup } from "@common/types/invoice";
import {
    ScheduleBillingRowInfo,
    buildScheduleBillingRowInfo,
} from "./scheduleBillingRowHelpers";
import { buildUnpostedMilestoneDescription } from "./milestoneRowHelpers";
import { buildReversedPeriodDescription } from "./reversedPeriodDescription";
import { computeRemainingBalance } from "./scheduleRemainingBalance";

export const SCHEDULE_APPLICATIONS_EMPTY_VALUE = "-";

export interface ScheduleApplicationJournalEntryRef {
    referenceNumber: string;
    label: string;
}

export interface ScheduleApplicationParentRow {
    id: string;
    /** Set when `id` is a line-item display slice of a multi-line application. */
    scheduleApplicationId?: string;
    rowType: "application";
    templateId: string;
    templateName: string;
    group: string;
    date: string;
    specificDate: string | null;
    termLength: number;
    termUnit: ScheduleTermUnit;
    sourceType: ScheduleSourceType;
    revenueSubMode: RevenueSubMode | null;
    status: ScheduleApplicationStatus;
    isModified: boolean;
    isMilestone: boolean;
    reference: ScheduleApplicationJournalEntryRef | null;
    description: string;
    /** The schedule's source as named by the API; slices keep it while `description` names the line. */
    sourceLabel: string;
    lineItemLabel: string | null;
    isLineScoped: boolean;
    counterpartyName: string | null;
    classIds: string[];
    entityId: number;
    ledgerId: string;
    ledgerBasis: AccountingLedgerBasis;
    splitLabel: string | null;
    amount: number;
    /** The whole schedule's amount; `amount` is this slice's share on a line row. */
    applicationAmount: number;
    recognizedAmount: number;
    remainingAmount: number;
    remainingAfterStopAmount: number;
    matchTarget: ScheduleMatchTarget | null;
    relatedLinks: ScheduleApplicationRelatedLink[];
    hasPendingPeriods: boolean;
    hasPostedPeriods: boolean;
    billing: ScheduleBillingRowInfo;
    children: ScheduleApplicationChildRow[];
}

export interface ScheduleApplicationPeriodRow {
    id: string;
    rowType: "period";
    scheduleApplicationId: string;
    applicationStatus: ScheduleApplicationStatus;
    milestone: ScheduleApplicationListMilestone | null;
    date: string;
    /** Ladder generation the row belongs to; a bare period counts as the latest. */
    revision: number;
    periodStatus: ScheduleApplicationPeriodStatus;
    /** A posted month whose entry a reversal row has since cancelled. */
    isReversed: boolean;
    subtype: ScheduleJeSubtype;
    reference: ScheduleApplicationJournalEntryRef | null;
    /** The JE description once posted; otherwise the wording that month would carry. */
    description: string;
    counterpartyName: string | null;
    classIds: string[];
    entityId: number;
    ledgerId: string;
    ledgerBasis: AccountingLedgerBasis;
    splitLabel: string | null;
    amount: number;
    remainingAmount: number | null;
    children?: never;
}

export interface ScheduleApplicationLifecycleJeRow {
    id: string;
    rowType: "lifecycleJe";
    date: string;
    revision: number;
    subtype: ScheduleJeSubtype;
    reference: ScheduleApplicationJournalEntryRef | null;
    description: string;
    counterpartyName: string | null;
    classIds: string[];
    entityId: number;
    ledgerId: string;
    ledgerBasis: AccountingLedgerBasis;
    splitLabel: string | null;
    amount: number;
    /** Set when the entry moves the balance: a catch-up or a stop settlement. */
    remainingAmount: number | null;
    children?: never;
}

/** The ladder under an application: its periods and its lifecycle entries. */
export type ScheduleApplicationChildRow =
    | ScheduleApplicationPeriodRow
    | ScheduleApplicationLifecycleJeRow;

export type ScheduleApplicationRow =
    | ScheduleApplicationParentRow
    | ScheduleApplicationChildRow;

export type ScheduleMatchTarget =
    | { documentType: "invoice" | "bill"; documentId: string }
    | { transactionId: number };

export type ScheduleSourceMenuItem =
    | {
          label: "Go to transaction";
          action: "transaction";
          transactionId: number;
      }
    | {
          label: "Go to invoice";
          action: "invoice";
          invoiceId: string;
          groupBy: InvoiceViewOptionGroup;
      }
    | { label: "Go to bill"; action: "bill"; billId: string }
    | { label: "Go to manual schedule"; action: "manual" };

export function getScheduleSourceMenuItem(
    row: Pick<ScheduleApplicationParentRow, "sourceType" | "matchTarget"> &
        Partial<Pick<ScheduleApplicationParentRow, "lineItemLabel">>,
): ScheduleSourceMenuItem | null {
    if (row.sourceType === ScheduleSourceType.manual) {
        return { label: "Go to manual schedule", action: "manual" };
    }
    if (!row.matchTarget) {
        return null;
    }
    if ("transactionId" in row.matchTarget) {
        return {
            label: "Go to transaction",
            action: "transaction",
            transactionId: row.matchTarget.transactionId,
        };
    }
    if (row.matchTarget.documentType === "invoice") {
        return {
            label: "Go to invoice",
            action: "invoice",
            invoiceId: row.matchTarget.documentId,
            groupBy: row.lineItemLabel
                ? InvoiceViewOptionGroup.LINE_ITEMS
                : InvoiceViewOptionGroup.INVOICE,
        };
    }
    return {
        label: "Go to bill",
        action: "bill",
        billId: row.matchTarget.documentId,
    };
}

export function getScheduleMatchTarget(
    source: ScheduleApplicationSourceSummary,
): ScheduleMatchTarget | null {
    if (source.sourceType === ScheduleSourceType.invoice && source.invoiceId) {
        return { documentType: "invoice", documentId: source.invoiceId };
    }
    if (source.sourceType === ScheduleSourceType.bill && source.billId) {
        return { documentType: "bill", documentId: source.billId };
    }
    if (
        source.sourceType === ScheduleSourceType.transaction &&
        source.transactionId != null
    ) {
        return { transactionId: source.transactionId };
    }
    return null;
}

export function hasScheduleApplicationClasses(
    row: ScheduleApplicationRow,
): row is
    | ScheduleApplicationParentRow
    | ScheduleApplicationPeriodRow
    | ScheduleApplicationLifecycleJeRow {
    return (
        row.rowType === "application" || isScheduleApplicationDrillDownRow(row)
    );
}

export function isScheduleApplicationDrillDownRow(
    row: ScheduleApplicationRow,
): row is ScheduleApplicationPeriodRow | ScheduleApplicationLifecycleJeRow {
    return row.rowType === "period" || row.rowType === "lifecycleJe";
}

/**
 * A month with no entry reads the way its entry would: a dropped month as a
 * void, a future month as the recognition it is waiting to post.
 */
export function buildUnpostedPeriodDescription(options: {
    periodStatus: ScheduleApplicationPeriodStatus;
    sourceLabel: string;
}): string {
    return options.periodStatus === ScheduleApplicationPeriodStatus.voided
        ? `Revenue recognition voided: ${options.sourceLabel}`
        : `Revenue recognition schedule: ${options.sourceLabel}`;
}

/**
 * The parent row points at the source's own entry - the invoice's or the
 * transaction's - whose holding-account line the schedule re-coded. A manual
 * schedule has no source entry, so it falls back to its creation entry.
 */
function getParentJournalEntryReference(
    application: ScheduleApplicationListItem,
): ScheduleApplicationJournalEntryRef | null {
    const journalEntry =
        application.sourceJournalEntry ?? application.creationJournalEntry;
    return journalEntry ? formatJournalEntryReference(journalEntry) : null;
}

function childRowRank(row: ScheduleApplicationChildRow): number {
    return row.rowType === "period" ? 0 : 1;
}

export function formatJournalEntryReference(
    journalEntry: ScheduleApplicationListJournalEntry,
): ScheduleApplicationJournalEntryRef {
    return {
        referenceNumber: journalEntry.referenceNumber,
        label: getShortJournalEntryReferenceNumber(
            journalEntry.referenceNumber,
        ),
    };
}

export function mapApplicationToRow(
    application: ScheduleApplicationListItem,
    options: { asOfDate?: string } = {},
): ScheduleApplicationParentRow {
    const today = formatISO(new Date(), { representation: "date" });
    const asOfDate = options.asOfDate ?? today;
    const ladderPeriodIds = new Set(
        application.periods.map((period) => period.id),
    );
    const remainingAmount = computeRemainingBalance({
        application,
        ladderPeriodIds,
        asOfDate,
    });
    const remainingAfterStopAmount = computeRemainingBalance({
        application,
        ladderPeriodIds,
        asOfDate: today,
    });
    // What each row that moves the balance contributes, keyed by row id; the
    // running balance is taken over the rows in display order below.
    const balanceMoves = new Map<string, number>();
    const hasRunningBalance =
        application.status !== ScheduleApplicationStatus.unwound;
    // A period with no entry (voided or never posted) belongs to the ladder
    // as it stands now, so it sorts by date among the latest generation.
    const latestRevision = Math.max(
        0,
        ...application.periods.flatMap((period) =>
            period.journalEntries.map((journalEntry) => journalEntry.revision),
        ),
        ...(application.lifecycleJournalEntries ?? []).map(
            (entry) => entry.revision,
        ),
    );
    const periodRows = application.periods.flatMap(
        (period): ScheduleApplicationPeriodRow[] => {
            const milestone = period.milestone ?? null;
            const common = {
                rowType: "period" as const,
                scheduleApplicationId: application.id,
                applicationStatus: application.status,
                milestone,
                date: period.effectiveDate,
                periodStatus: period.status,
                isReversed: false,
                subtype: period.subtype,
                entityId: application.entityId,
                ledgerId: application.ledgerId,
                ledgerBasis: application.ledgerBasis,
            };
            if (period.journalEntries.length === 0) {
                return [
                    {
                        ...common,
                        id: period.id,
                        revision: latestRevision,
                        reference: null,
                        description: milestone
                            ? buildUnpostedMilestoneDescription({
                                  periodStatus: period.status,
                                  applicationStatus: application.status,
                                  milestone,
                              })
                            : buildUnpostedPeriodDescription({
                                  periodStatus: period.status,
                                  sourceLabel: application.source.label,
                              }),
                        counterpartyName: null,
                        classIds: [],
                        splitLabel: null,
                        amount: period.amount,
                        remainingAmount: null,
                    },
                ];
            }
            const isLive = isLiveLadderPeriod(period);
            const liveJournalEntryId = isLive ? period.journalEntry?.id : null;
            if (liveJournalEntryId && hasRunningBalance) {
                balanceMoves.set(liveJournalEntryId, period.amount);
            }
            // One row per generation: a re-spread month shows its original
            // recognition and its replacement, each at its own amount. Only
            // the live one carries the running balance and, once a reversal
            // row cancels the month, reads as reversed.
            return period.journalEntries.map((journalEntry) => {
                const isReversed =
                    period.reversedByPeriodId !== null &&
                    journalEntry.id === period.journalEntry?.id;
                return {
                    ...common,
                    id: journalEntry.id,
                    revision: journalEntry.revision,
                    isReversed,
                    reference: formatJournalEntryReference(journalEntry),
                    description: isReversed
                        ? buildReversedPeriodDescription(
                              journalEntry.description,
                          )
                        : journalEntry.description,
                    counterpartyName: application.counterparty?.name ?? null,
                    classIds: application.classIds,
                    splitLabel: journalEntry.splitLabel,
                    amount: journalEntry.amount,
                    remainingAmount: null,
                };
            });
        },
    );
    const lifecycleRows = (application.lifecycleJournalEntries ?? []).map(
        (entry): ScheduleApplicationLifecycleJeRow => {
            if (
                hasRunningBalance &&
                isScheduleBalanceAdjustmentEntry({ entry, ladderPeriodIds })
            ) {
                balanceMoves.set(entry.id, entry.amount);
            }
            return {
                id: entry.id,
                rowType: "lifecycleJe",
                date: entry.dateOnly ?? application.termStart,
                revision: entry.revision,
                subtype: entry.subtype,
                reference: formatJournalEntryReference(entry),
                description: entry.description,
                counterpartyName: application.counterparty?.name ?? null,
                classIds: application.classIds,
                entityId: application.entityId,
                ledgerId: application.ledgerId,
                ledgerBasis: application.ledgerBasis,
                splitLabel: entry.splitLabel,
                amount: entry.amount,
                remainingAmount: null,
            };
        },
    );
    // Oldest generation first, then by month, with a month's recognition
    // ahead of the reversal that cancels it - so a change reads as the whole
    // original ladder being undone, followed by the new one.
    const sortedRows = [...periodRows, ...lifecycleRows].sort(
        (left, right) =>
            left.revision - right.revision ||
            left.date.localeCompare(right.date) ||
            childRowRank(left) - childRowRank(right),
    );
    let recognizedSoFar = 0;
    const ledgerRows = sortedRows.map((row): ScheduleApplicationChildRow => {
        const move = balanceMoves.get(row.id);
        if (move === undefined) {
            return row;
        }
        recognizedSoFar = roundToCents(recognizedSoFar + move);
        return {
            ...row,
            remainingAmount: roundToCents(application.amount - recognizedSoFar),
        };
    });
    return {
        id: application.id,
        rowType: "application",
        templateId: application.scheduleTemplateId,
        templateName: application.scheduleTemplateName,
        group: application.group,
        date: application.termStart,
        specificDate: application.specificDate,
        termLength: application.termLength,
        termUnit: application.termUnit,
        sourceType: application.sourceType,
        revenueSubMode: application.revenueSubMode,
        status: application.status,
        isModified: application.isModified,
        isMilestone: application.isMilestone ?? false,
        reference: getParentJournalEntryReference(application),
        description: application.source.label,
        sourceLabel: application.source.label,
        lineItemLabel: null,
        isLineScoped: (application.sourceLineIds?.length ?? 0) > 0,
        counterpartyName: application.counterparty?.name ?? null,
        classIds: application.classIds,
        entityId: application.entityId,
        ledgerId: application.ledgerId,
        ledgerBasis: application.ledgerBasis,
        splitLabel: application.splitLabel,
        amount: application.amount,
        applicationAmount: application.amount,
        recognizedAmount: application.recognizedAmount,
        remainingAmount,
        remainingAfterStopAmount,
        matchTarget: getScheduleMatchTarget(application.source),
        relatedLinks: application.relatedLinks,
        hasPendingPeriods: application.periods.some(
            (period) =>
                period.status === ScheduleApplicationPeriodStatus.pending,
        ),
        hasPostedPeriods: application.periods.some(
            (period) =>
                period.status === ScheduleApplicationPeriodStatus.posted,
        ),
        billing: buildScheduleBillingRowInfo(application),
        children: ledgerRows,
    };
}

function allocateProportionalAmounts(
    total: number,
    partAmounts: number[],
): number[] {
    if (partAmounts.length === 0) {
        return [];
    }
    const totalParts = partAmounts.reduce(
        (sum, amount) => sum + Math.abs(amount),
        0,
    );
    if (totalParts <= 0) {
        return partAmounts.map(() => 0);
    }
    let allocated = 0;
    return partAmounts.map((partAmount, index) => {
        if (index === partAmounts.length - 1) {
            return roundToCents(total - allocated);
        }
        const slice = roundToCents((total * Math.abs(partAmount)) / totalParts);
        allocated += slice;
        return slice;
    });
}

function scopeChildRowsToLineSlice<T extends { id: string }>(
    children: T[],
    lineSliceId: string,
): T[] {
    return children.map((child) => ({
        ...child,
        id: `${lineSliceId}__${child.id}`,
    }));
}

export function expandScheduleApplicationRowsForDisplay(
    row: ScheduleApplicationParentRow,
    sourceLines: ScheduleApplicationSourceLine[] | undefined,
): ScheduleApplicationParentRow[] {
    if (!sourceLines || sourceLines.length <= 1) {
        return [row];
    }
    const lineAmounts = sourceLines.map((line) => line.amount);
    const amountByLine = allocateProportionalAmounts(row.amount, lineAmounts);
    const recognizedByLine = allocateProportionalAmounts(
        row.recognizedAmount,
        lineAmounts,
    );
    const remainingByLine = allocateProportionalAmounts(
        row.remainingAmount,
        lineAmounts,
    );
    return sourceLines.map((line, index) => {
        const lineSliceId = `${row.id}__line__${line.id}`;
        return {
            ...row,
            id: lineSliceId,
            scheduleApplicationId: row.id,
            description: line.description,
            lineItemLabel: line.description,
            splitLabel: line.splitLabel,
            amount: amountByLine[index],
            recognizedAmount: recognizedByLine[index],
            remainingAmount: remainingByLine[index],
            children: scopeChildRowsToLineSlice(row.children, lineSliceId),
        };
    });
}

function enrichApplicationRowWithSourceLines(
    row: ScheduleApplicationParentRow,
    sourceLines: ScheduleApplicationSourceLine[] | undefined,
): ScheduleApplicationParentRow {
    if (!sourceLines || sourceLines.length !== 1) {
        return row;
    }
    const line = sourceLines[0];
    return {
        ...row,
        lineItemLabel: line.description,
        description: line.description,
        splitLabel: line.splitLabel ?? row.splitLabel,
    };
}

export function mapApplicationToDisplayRows(
    application: ScheduleApplicationListItem,
    options: { asOfDate?: string } = {},
): ScheduleApplicationParentRow[] {
    const row = enrichApplicationRowWithSourceLines(
        mapApplicationToRow(application, options),
        application.sourceLines,
    );
    return expandScheduleApplicationRowsForDisplay(
        row,
        application.sourceLines,
    );
}

export interface ScheduleApplicationsFilters {
    templateIds: string[];
    groups: string[];
    sourceTypes: ScheduleSourceType[];
    revenueSubModes: RevenueSubMode[];
    expenseSubModes: ExpenseSubMode[];
    entityIds: number[];
    lifecycleStatuses: ScheduleLifecycleFilterStatus[];
    billingStatuses: ScheduleBillingStatus[];
    counterpartyNames: string[];
    lineItemLabels: string[];
    classIds: string[];
    periodStart: string | null;
    periodEnd: string | null;
}

export const EMPTY_SCHEDULE_APPLICATIONS_FILTERS: ScheduleApplicationsFilters =
    {
        templateIds: [],
        groups: [],
        sourceTypes: [],
        revenueSubModes: [],
        expenseSubModes: [],
        entityIds: [],
        lifecycleStatuses: [],
        billingStatuses: [],
        counterpartyNames: [],
        lineItemLabels: [],
        classIds: [],
        periodStart: null,
        periodEnd: null,
    };

export interface ScheduleApplicationTemplateOption {
    id: string;
    name: string;
}

export function matchesLineItemLabels(
    row: Pick<ScheduleApplicationParentRow, "lineItemLabel">,
    lineItemLabels: string[],
): boolean {
    return (
        lineItemLabels.length === 0 ||
        (row.lineItemLabel !== null &&
            lineItemLabels.includes(row.lineItemLabel))
    );
}
